import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import type { ModuleFormField, ModulePageDto } from '@cpm/shared';
import { api } from '../api/client.js';
import {
  Button,
  Card,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '../components/ui.js';

/**
 * Generic server-driven module page (G21, ADR-0035) — the interim UI for
 * out-of-tree modules. A module that cannot ship React into the SPA bundle
 * describes its page (`GET /api/<key>/page` → ModulePageDto) as read-only info
 * rows plus an optional config form; this component renders any such page and
 * posts edited values back (`PUT /api/<key>/page`). Reached at `/m/:moduleKey`.
 */

type FormValue = string | number | boolean | null;

function FieldControl({
  field,
  value,
  onChange,
}: {
  field: ModuleFormField;
  value: FormValue;
  onChange: (v: FormValue) => void;
}) {
  const id = `field-${field.key}`;
  switch (field.type) {
    case 'textarea':
      return (
        <Textarea
          id={id}
          value={value == null ? '' : String(value)}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'number':
      return (
        <Input
          id={id}
          type="number"
          value={value == null ? '' : String(value)}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case 'boolean':
      return (
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            id={id}
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
            className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-800"
          />
          <span className="text-slate-600 dark:text-slate-300">Enabled</span>
        </label>
      );
    case 'select':
      return (
        <Select id={id} value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)}>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      );
    default:
      return (
        <Input
          id={id}
          value={value == null ? '' : String(value)}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

export function GenericModulePage() {
  const { moduleKey = '' } = useParams();
  const queryClient = useQueryClient();
  const page = useQuery({
    queryKey: ['module-page', moduleKey],
    queryFn: () => api.get<ModulePageDto>(`/api/${moduleKey}/page`),
    enabled: moduleKey.length > 0,
  });

  const [values, setValues] = useState<Record<string, FormValue>>({});
  // Seed the form from the server whenever the page (re)loads.
  useEffect(() => {
    if (page.data) setValues(page.data.values);
  }, [page.data]);

  const save = useMutation({
    mutationFn: (v: Record<string, FormValue>) => api.put(`/api/${moduleKey}/page`, v),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['module-page', moduleKey] }),
  });

  if (page.isPending) return <Spinner />;
  if (page.error) return <ErrorNote error={page.error} />;

  const dto = page.data;
  const editable = dto.submitLabel != null && dto.fields.length > 0;

  return (
    <div>
      <PageHeader title={dto.title} description={dto.description} />

      {dto.info && dto.info.length > 0 && (
        <Card className="mb-4 p-5">
          <dl className="grid gap-3 sm:grid-cols-2">
            {dto.info.map((row) => (
              <div key={row.label}>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  {row.label}
                </dt>
                <dd className="mt-0.5 text-sm text-slate-800 dark:text-slate-200">{row.value}</dd>
              </div>
            ))}
          </dl>
        </Card>
      )}

      {editable && (
        <Card className="p-5">
          {save.error != null && (
            <div className="mb-4">
              <ErrorNote error={save.error} />
            </div>
          )}
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(values);
            }}
          >
            {dto.fields.map((field) => (
              <div key={field.key}>
                <Label htmlFor={`field-${field.key}`}>{field.label}</Label>
                <div className="mt-1">
                  <FieldControl
                    field={field}
                    value={values[field.key] ?? null}
                    onChange={(v) => setValues((prev) => ({ ...prev, [field.key]: v }))}
                  />
                </div>
                {field.help && (
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{field.help}</p>
                )}
              </div>
            ))}
            <div className="flex items-center gap-3">
              <Button type="submit" loading={save.isPending}>
                {dto.submitLabel}
              </Button>
              {save.isSuccess && !save.isPending && (
                <span className="text-sm text-green-600 dark:text-green-400">Saved.</span>
              )}
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
